import { ComponentProps, ReactElement, useContext, useEffect, useState } from 'react';
import cuid from 'cuid';
import { Window } from '../../components/window/Window';
import { WindowStackContext } from './WindowStack';

export const WindowStackItem = ({ children }: Props) => {

    const push = usePushWindow();
    const pop = usePopWindow();
    const update = useUpdateWindow();
    const [key] = useState(() => cuid());

    useEffect(() => {
        push(key, children);
        return () => pop(key);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        update(key, children);
    }, [children, key]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

type Props = {
    children: ReactElement<ComponentProps<typeof Window>>;
};

const usePushWindow = () => {
    const { setWindowStack } = useContext(WindowStackContext);
    return (key: string, element: ReactElement<ComponentProps<typeof Window>>) => {
        setWindowStack(previousState => [...previousState, { key, element }]);
    };
};

const usePopWindow = () => {
    const { setWindowStack } = useContext(WindowStackContext);
    return (key: string) => {
        setWindowStack(previousState => previousState.filter(i => i.key !== key));
    };
};

// This updates the 'element' whenever it receives new props from its parent.
// Otherwise, the WindowStackContext would keep the props from the initial push
const useUpdateWindow = () => {
    const { setWindowStack } = useContext(WindowStackContext);
    return (key: string, element: ReactElement<ComponentProps<typeof Window>>) => {
        setWindowStack(previousState => previousState
            .map(p => p.key === key ? { key, element } : p));
    };
};